import { NextPage } from 'next'
import { withPasswordProtect } from 'next-password-protect'
import { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import { ReactElement, ReactNode, useEffect } from 'react'
import { CookiesProvider, useCookies } from 'react-cookie'
import { ThemeProvider } from 'styled-components'
import { GlobalStyles } from 'styles/globalStyles'
import { theme } from 'styles/theme'

import { AuthContextProvider } from '@/context/authContext'
import { BannerProvider } from '@/context/bannerContext'
import { BasketProvider } from '@/context/basketContext'
import { CookieConsentContextProvider } from '@/context/cookieConsentContext'
import { CustomerContextProvider } from '@/context/customerContext'
import { GeoContextProvider } from '@/context/geoContext'
import { LeadProvider } from '@/context/leadContext'
import { LocaleProvider } from '@/context/localeContext'
import { TrackingProvider } from '@/context/trackingContext'

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

// Deactivated for now
// eslint-disable-next-line no-constant-condition, no-constant-binary-expression
if (false && process.env.NODE_ENV === 'development') {
  require('mocks')
}

function App({ Component, pageProps }: AppPropsWithLayout) {
  const ENABLE_OPTIMIZELY = process.env.ENABLE_OPTIMIZELY === 'true'

  const router = useRouter()
  const [cookies] = useCookies(['OptanonConsent'])

  const hasAnalyticsConsent = () => {
    const consentCookie = cookies.OptanonConsent
    return consentCookie && consentCookie.indexOf('C0002:1') !== -1
  }

  useEffect(() => {
    const brazeApiKey = process.env.NEXT_PUBLIC_BRAZE_API_KEY
    const brazeSdkEndpoint = process.env.BRAZE_SDK_ENDPOINT
    if (brazeApiKey && brazeSdkEndpoint) {
      import('@/lib/braze/braze-exports').then(({ initialize, openSession, automaticallyShowInAppMessages }) => {
        initialize(brazeApiKey, {
          baseUrl: brazeSdkEndpoint,
          enableLogging: process.env.NEXT_PUBLIC_ZOE_ENV !== 'PROD' && process.env.NEXT_ENABLE_BRAZE_LOGGING === 'true',
          allowUserSuppliedJavascript: true,
        })
        automaticallyShowInAppMessages()
        openSession()
      })
    }
    if (ENABLE_OPTIMIZELY && typeof window !== 'undefined') {
      // Hold Optimizely events if analytics consent is not given
      if (!hasAnalyticsConsent()) {
        window['optimizely'] = window['optimizely'] || []
        window['optimizely'].push({
          type: 'holdEvents',
        })
        console.log('Optimizely events on hold until consent is given')
      }
    }
  }, []) // Only run on initial load

  // Optimizely route change activation
  useEffect(() => {
    if (ENABLE_OPTIMIZELY && typeof window !== 'undefined') {
      const handleRouteChange = () => {
        // Only activate Optimizely if analytics consent is given
        if (hasAnalyticsConsent()) {
          window['optimizely'] = window['optimizely'] || []
          window['optimizely'].push({
            type: 'activate',
          })
          console.log('Optimizely activated on route change')
        } else {
          console.log('Optimizely activation skipped - no analytics consent')
        }
      }

      // Listen for route changes
      router.events.on('routeChangeComplete', handleRouteChange)

      // Cleanup listener on unmount
      return () => {
        router.events.off('routeChangeComplete', handleRouteChange)
      }
    }
  }, [router.events, cookies.OptanonConsent])

  // Optimizely page activation on route changes
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window['optimizely'] && window['optimizely'].get) {
        const optlyPages = window['optimizely'].get('data')?.pages

        if (optlyPages && Object.keys(optlyPages).length > 0) {
          Object.keys(optlyPages).forEach((pageId) => {
            if (optlyPages[pageId]?.apiName) {
              // Deactivate page
              window['optimizely']?.push({
                type: 'page',
                pageName: optlyPages[pageId].apiName,
                isActive: false,
              })

              // Reactivate page
              window['optimizely']?.push({
                type: 'page',
                pageName: optlyPages[pageId].apiName,
              })
            }
          })
        }
      }
    } catch (error) {
      console.error('Error in Optimizely page activation:', error)
    }
  }, [ENABLE_OPTIMIZELY])

  const getLayout = Component.getLayout ?? ((page) => page)
  return (
    <>
      <AuthContextProvider>
        <CustomerContextProvider>
          <CookiesProvider>
            <GeoContextProvider>
              <TrackingProvider>
                <CookieConsentContextProvider>
                  <BannerProvider>
                    <LeadProvider>
                      <BasketProvider>
                        <LocaleProvider>
                          <GlobalStyles />
                          <ThemeProvider theme={theme}>{getLayout(<Component {...pageProps} />)}</ThemeProvider>
                        </LocaleProvider>
                      </BasketProvider>
                    </LeadProvider>
                  </BannerProvider>
                </CookieConsentContextProvider>
              </TrackingProvider>
            </GeoContextProvider>
          </CookiesProvider>
        </CustomerContextProvider>
      </AuthContextProvider>
    </>
  )
}

export default process.env.PASSWORD_PROTECTED_ENVIRONMENT
  ? withPasswordProtect(App, {
      loginComponentProps: {
        buttonBackgroundColor: '#FA7268',
        buttonColor: '#FFFFFF',
        logo: '/assets/img/zoe-logo.svg',
      },
    })
  : App
